import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

import LoadingScreen from 'components/container-components/LoadingScreen';

// The @mercoa/react bundle is only needed for US companies with AP or AR
// enabled. Loading it eagerly on every page parsed hundreds of KB of JS
// that the majority of tenants never use. dynamic() defers it until
// MercoaSession actually renders (i.e. token is set below).
// loading fallback is required because MercoaProvider wraps the whole app:
// without it, the chunk-load window between "token set" and "MercoaSession
// mounted" renders null and the entire app below blanks out.
const MercoaSession = dynamic(() => import('@mercoa/react').then((m) => ({ default: m.MercoaSession })), {
  ssr: false,
  loading: () => <LoadingScreen />,
});
import theraToast from 'components/generic-components/toast/TheraToast';
import { useTheraFernClient } from 'hooks/useFern';
import { formatErrorMessage, logError } from 'utils/error';
import { isStytchProdEnv, isSuperAdminMode } from 'utils/user';
import { useCompany } from './company';
import { useUser } from './user';

export const MercoaProvider = ({ children }) => {
  const { theraFernApi } = useTheraFernClient();
  const { user, roleType } = useUser();
  const { company } = useCompany();
  const [session, setSession] = useState<{ token: string; owner: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const isUSCompany = company?.address?.country === 'US';
  const isAP = company?.settings?.enabledApps?.ap;
  const isAR = company?.settings?.enabledApps?.ar;
  // AP and AR pages are manager pages, so a worker would sit on the loader below for a token they never use.
  const isManager = roleType === 'manager';
  // Logging out doesn't reload the page, so a token is only used for the user and company it was fetched for,
  // or the next person to sign in on this tab would get the last one's Mercoa session.
  const owner = `${user?.userID}:${company?.companyID}`;
  const token = isManager && session?.owner === owner ? session.token : undefined;
  // Forgotten when its owner goes rather than only hidden, or the same person signing back in on this tab would
  // get the old token straight back.
  useEffect(() => () => setSession(undefined), [owner]);

  useEffect(() => {
    if (!theraFernApi || !user?.userID || !company?.companyID) return;
    if (!isManager || !isUSCompany || (!isAP && !isAR)) return;
    if (token) return;

    // A request still running when the user or company changes answers for the wrong owner, so it's dropped
    // here and the new owner gets a request of their own.
    let isCurrent = true;
    setIsLoading(true);
    theraFernApi.thera.company.companyApi
      .getMercoaToken(company.companyID, {
        userId: user.userID,
        expiresIn: '3d',
      })
      .then((resp) => {
        if (isCurrent) setSession({ token: resp.token, owner });
      })
      .catch((err) => {
        if (!isCurrent) return;
        // thera-override: thera-toast-log-error-redundancy
        logError(err, { source: 'MercoaProvider' });
        if (!isStytchProdEnv || isSuperAdminMode) {
          theraToast.warning(`Failed to get Mercoa token: ${formatErrorMessage(err.body, '')}`, {
            id: 'mercoa-token-error',
          });
        }
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
      setIsLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, isUSCompany, isAP, isAR, isManager]);

  if (isLoading) return <LoadingScreen />;

  return token ? <MercoaSession token={token}>{children}</MercoaSession> : children;
};
